<template>
  <div class="container">
    <h1>Fichiers</h1>

    <div class="row">
      <div class="col-md-3">
        <b-card bg-variant="light">
          <b-card-title class="h5 mb-0 font-weight-normal"
            >Contenu</b-card-title
          >
          <b-card-text class="small text-muted">Dernière version</b-card-text>
          <b-card-text class="font-weight-bold lead text-info">{{
            latestVersions.data ? latestVersions.data.version : 'Inconnu'
          }}</b-card-text>
        </b-card>
      </div>
      <div class="col-md-3">
        <b-card bg-variant="light">
          <b-card-title class="h5 mb-0 font-weight-normal">App</b-card-title>
          <b-card-text class="small text-muted">Dernière version</b-card-text>
          <b-card-text class="font-weight-bold lead text-info">{{
            latestVersions.package ? latestVersions.package.version : 'Inconnu'
          }}</b-card-text>
        </b-card>
      </div>
    </div>

    <b-alert variant="warning" show class="mt-4">
      <p class="m-0">
        Lorsqu'une salle met à jour son contenu, l'ordre d'importation est le
        suivant:
        <span class="font-weight-bold">Jeu > Question > DMX > Médias</span>.
      </p>
    </b-alert>

    <hr />

    <div class="mt-2 d-flex justify-content-between">
      <p>Filtrer par:</p>
      <div class="inline-block">
        <b-button size="sm" variant="primary" class="ml-1" v-b-modal.add-file
          >Ajouter un nouveau fichier</b-button
        >
      </div>
    </div>
    <div class="d-flex justify-content-start" style="gap: 20px">
      <b-form-group class="" label="Type">
        <b-form-select v-model="type" @change="fetchVersions()">
          <option v-for="(name, value) in types" :key="value" :value="value">
            {{ name }}
          </option>
        </b-form-select>
      </b-form-group>
      <b-form-group class="" label="Langue">
        <b-form-select v-model="language" @change="fetchVersions()">
          <option
            v-for="(name, value) in languages"
            :key="value"
            :value="value"
          >
            {{ name }}
          </option>
        </b-form-select>
      </b-form-group>
    </div>

    <VersionsTable
      :list="list"
      :fields-only="[
        'id',
        'type',
        'language',
        'version',
        'description',
        'options',
        'preRelease',
        'createdAt',
        'actions',
      ]"
      :general-filters="{ versions: [], language: '' }"
      @deleted="onVersionDelete"
    />

    <b-modal
      id="add-file"
      title="Ajouter un nouveau fichier"
      :hide-footer="true"
      ref="add-file-modal"
    >
      <div class="mb-5">
        <p class="bg-light p-2">
          Ajouter un nouveau fichier ZIP contenant les fichiers à ajouter et/ou
          à remplacer.
        </p>
        <b-alert
          :show="newFileWarningMessage !== null"
          variant="warning"
          dismissible
          @dismissed="newFileWarningMessage = null"
        >
          <p class="small m-0">{{ newFileWarningMessage }}</p>
        </b-alert>
        <b-alert :show="newFileErrorMessage !== null" variant="danger">
          <p class="small m-0" v-html="newFileErrorMessage" />
        </b-alert>
        <b-alert :show="newFileFormReady === true" variant="success">
          <p class="small m-0">
            Tout semble conforme, vous pouvez maintenant envoyer le fichier.
          </p>
        </b-alert>

        <b-alert show variant="info">
          <div class="small m-0">
            Le fichier ZIP <strong>PEUT</strong> contenir:
            <ul class="m-0 mb-2">
              <li>
                Un fichier <strong>encodé</strong> nommé
                <code>questions.txt</code> à la racine
              </li>
              <li>Un fichier nommé <code>questions.xlsx</code> à la racine</li>
              <li>
                Un fichier <strong>encodé</strong> nommé
                <code>games.txt</code> à la racine
              </li>
              <li>
                Un fichier nommé <code>dmx.txt</code> à la racine
                <i>(coming soon)</i>
              </li>
            </ul>
            <ul class="m-0">
              <li>Un dossier <code>assets/images</code></li>
              <li>Un dossier <code>assets/videos</code></li>
              <li>Un dossier <code>assets/sounds</code></li>
              <li>Un dossier <code>uploads/images</code></li>
              <li>Un dossier <code>uploads/videos</code></li>
              <li>Un dossier <code>uploads/sounds</code></li>
            </ul>
          </div>
          <div class="small mt-3">
            Si vous ajoutez un fichier <code>questions.txt</code> ou
            <code>questions.xslx</code>,
            <strong
              >les anciennes questions par défaut seront supprimées</strong
            >
            avant d'ajouter les nouvelles.
          </div>
        </b-alert>
      </div>

      <div v-if="uploadingPercent !== null" class="mb-4">
        <h5>Progression de l'envoi</h5>
        <b-progress
          :value="uploadingPercent"
          :max="100"
          :label="`${uploadingPercent}%`"
          variant="info"
          show-progress
          animated
        ></b-progress>
      </div>

      <b-form @submit.prevent="submitNewFile">
        <b-form-group
          label="Fichier ZIP:"
          description="Sélectionnez le fichier ZIP avec le nouveau contenu. Les champs suivants seront automatiquement pré-remplis si le nom du fichier contient le type et la version (exemple: question-2.0.0.zip)"
        >
          <b-form-file
            v-model="newFileForm.file"
            accept=".zip"
            placeholder="Choisissez un fichier"
            drop-placeholder="Déposez un fichier ici..."
            @change="newFileChanged"
          ></b-form-file>
        </b-form-group>

        <b-form-group label="Langue:">
          <b-form-select
            v-model="newFileForm.language"
            @change="resetNewFileErrors"
            :options="languagesSelect"
          ></b-form-select>
        </b-form-group>

        <b-form-group
          label="Version:"
          description="Il doit être au format X.Y.Z ou X.Y.Z-A. Exemple: 2.3.0 ou 3.1.2-18"
        >
          <b-form-input
            v-model="newFileForm.version"
            placeholder="Numéro de version"
            @change="resetNewFileErrors"
          ></b-form-input>
        </b-form-group>

        <b-form-group label="Description:">
          <b-form-textarea
            v-model="newFileForm.description"
            @change="resetNewFileErrors"
          ></b-form-textarea>
        </b-form-group>

        <hr />

        <b-form-group
          label="Supprimer les questions:"
          description="Saisissez les identifiants des questions que vous voulez supprimer. Un identifiant par ligne. Optionnel"
        >
          <b-form-textarea
            v-model="newFileForm.options.removeCustomQuestions"
            @change="resetNewFileErrors"
          ></b-form-textarea>
        </b-form-group>

        <div class="d-flex justify-content-between mt-5">
          <b-button
            type="button"
            variant="light"
            :disabled="isUploading"
            @click="cancelAddFile"
            >Annuler</b-button
          >
          <b-button
            v-if="newFileFormReady !== true"
            type="button"
            variant="primary"
            :disabled="isUploading"
            @click="verifyVersion"
            >Vérifier la version</b-button
          >
          <b-button
            v-else
            type="submit"
            variant="success"
            :disabled="isUploading"
            >Envoyer</b-button
          >
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
  import axios from 'axios'
  import { invert } from 'lodash'
  import VersionsTable from '@/components/VersionsTable.vue'
  import { getLanguageSelectOptions } from '@/constants/languages'

  export default {
    name: 'FilesPage',
    components: {
      VersionsTable,
    },
    data() {
      return {
        fileImages: null,
        fileSounds: null,
        fileVideos: null,
        fileGames: null,

        latestVersions: {
          data: null,
          package: null,
        },

        type: 'data',
        language: null,
        types: [],
        list: [],
        currentPage: 1,
        perPage: 10,

        newFileForm: {
          type: 'data',
          version: null,
          language: 'all',
          file: null,
          description: null,
          options: {
            removeCustomQuestions: '',
          },
        },
        newFileFormReady: false,
        uploadingPercent: null,
        newFileWarningMessage: null,
        newFileErrorMessage: null,

        zipContentLoading: false,
        zipContentLoadingError: null,
        zipContent: null,
      }
    },
    computed: {
      isUploading() {
        return this.uploadingPercent !== null
      },
      removeCustomQuestionsList() {
        return this.newFileForm.options.removeCustomQuestions
          .split('\n')
          .map((id) => id.trim())
          .filter((id) => id !== '')
      },
      languages() {
        return {
          null: '',
          all: 'Dans toutes les langues uniquement',
          ...getLanguageSelectOptions(),
        }
      },
      languagesSelect() {
        return {
          all: 'Toutes',
          ...getLanguageSelectOptions(),
        }
      },
    },
    mounted() {
      this.fetchAnalytics()
      this.fetchVersionsTypes()
      this.fetchVersions()
    },
    methods: {
      getOptionsPopoverContent(options) {
        return `
        <div class="d-flex flex-column">
          <div class="d-flex justify-content-between">
            <div>Supprimer les questions:</div>
            <div>${options.removeCustomQuestions}</div>
          </div>
        </div>
      `
      },
      fetchVersionsTypes() {
        axios
          .get('/version/types')
          .then((response) => {
            // this.types = invert(response.data).filter((type) => type !== 'package');
            this.types = invert(response.data)
          })
          .catch((error) => {
            console.log(error)
          })
      },
      fetchVersions() {
        // Add params to url using vue-router
        const params = {}
        if (this.type) {
          params['type'] = this.type
        }
        if (this.language !== null && this.language !== 'null') {
          params['language'] = this.language
        }

        this.$router.push({ query: params })

        axios
          .get('/version', {
            params: {
              type: params['type'] || null,
              language: params['language'] || null,
            },
          })
          .then((response) => {
            this.list = response.data
          })
          .catch((error) => {
            console.log(error)
          })
      },
      fetchAnalytics() {
        axios
          .get('/version/analytics')
          .then((response) => {
            Object.keys(this.latestVersions).forEach((type) => {
              if (response.data[type] && response.data[type].latest) {
                this.latestVersions[type] = response.data[type].latest
              } else {
                this.latestVersions[type] = null
              }
            })
          })
          .catch((error) => {
            console.log(error)
          })
      },
      verifyVersion() {
        const validated = this.validateNewFileForm()
        if (!validated) {
          return
        }

        axios
          .get('/version/available', {
            params: {
              type: this.newFileForm.type,
              version: this.newFileForm.version,
              language: this.newFileForm.language,
            },
          })
          .then((response) => {
            this.newFileFormReady = response.data.available
            if (!response.data.available) {
              this.newFileWarningMessage = 'Cette version existe déjà.'
            }
          })
          .catch((error) => {
            this.newFileErrorMessage = error.response.data.message
          })
      },
      cancelAddFile() {
        this.$refs['add-file-modal'].hide()
        this.clearNewFileForm()
        this.resetNewFileErrors()
      },
      clearNewFileForm() {
        this.newFileForm = {
          type: 'data',
          version: null,
          language: 'all',
          file: null,
          description: null,
          options: {
            removeCustomQuestions: '',
          },
        }

        this.newFileFormReady = false
      },
      resetNewFileErrors() {
        this.newFileWarningMessage = null
        this.newFileErrorMessage = null
      },
      typeChanged() {
        this.resetNewFileErrors()

        if (this.newFileForm.type !== 'question') {
          this.newFileForm.options.removeCustomQuestions = ''
        }
      },
      newFileChanged(event) {
        this.resetNewFileErrors()

        // On tente de deviner le type et la version du fichier, à partir de son nom
        const filename = event.target.files[0].name
        const filenameWithoutExtension = filename
          .split('.')
          .slice(0, -1)
          .join('.')

        let splitterPattern

        if (
          filenameWithoutExtension.includes('-') &&
          filenameWithoutExtension.split('-').length === 2
        ) {
          splitterPattern = '-'
        } else if (
          filenameWithoutExtension.includes('-v') &&
          filenameWithoutExtension.split('-v').length === 2
        ) {
          splitterPattern = '-v'
        } else if (
          filenameWithoutExtension.includes('_') &&
          filenameWithoutExtension.split('_').length === 2
        ) {
          splitterPattern = '_'
        } else if (
          filenameWithoutExtension.includes('_v') &&
          filenameWithoutExtension.split('_v').length === 2
        ) {
          splitterPattern = '_v'
        }

        if (splitterPattern) {
          // eslint-disable-next-line no-unused-vars
          const [type, version] =
            filenameWithoutExtension.split(splitterPattern)

          this.newFileForm.version = version
        } else {
          console.log('here')
          this.newFileWarningMessage =
            'Impossible de déterminer la version du fichier. Veuillez les renseigner manuellement.'
          console.log(this.newFileWarningMessage)
        }
      },
      validateNewFileForm() {
        this.resetNewFileErrors()

        if (!this.newFileForm.file) {
          this.newFileErrorMessage = 'Veuillez sélectionner un fichier.'
          return false
        }
        if (!this.newFileForm.type) {
          this.newFileErrorMessage = 'Veuillez renseigner le type du fichier.'
          return false
        }
        if (!this.newFileForm.version) {
          this.newFileErrorMessage =
            'Veuillez renseigner la version du fichier.'
          return false
        }
        if (!this.newFileForm.language) {
          this.newFileErrorMessage = 'Veuillez renseigner la langue du fichier.'
          return false
        }
        if (
          this.newFileForm.version.match(/^(\d+\.)?(\d+\.)?(\*|\d+)$/) === null
        ) {
          this.newFileErrorMessage =
            'La version du fichier doit correspondre au format "X.Y.Z" ou "X.Y.Z-A".'
          return false
        }

        return true
      },
      submitNewFile() {
        const validated = this.validateNewFileForm()
        if (!validated) {
          return
        }

        this.uploadingPercent = 0

        // FormData
        const formdata = new FormData()
        formdata.append('file', this.newFileForm.file)
        formdata.append('type', this.newFileForm.type)
        formdata.append('version', this.newFileForm.version)
        formdata.append('language', this.newFileForm.language)
        formdata.append('preRelease', this.newFileForm.version.includes('-'))
        formdata.append('description', this.newFileForm.description || '')
        for (const key in this.removeCustomQuestionsList) {
          formdata.append(
            'options[removeCustomQuestions][]',
            this.removeCustomQuestionsList[key]
          )
        }

        // Axios
        const config = {
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            )
            this.uploadingPercent = percentCompleted
          },
        }

        axios
          .post('/version', formdata, config)
          .then((response) => {
            console.log('success!', response)

            this.$notify({
              type: 'success',
              title: 'Fichier ajouté',
              text: 'Le fichier a bien été ajouté.',
            })

            this.$refs['add-file-modal'].hide()
            this.clearNewFileForm()
            this.resetNewFileErrors()
            this.fetchVersions()
            this.fetchAnalytics()
          })
          .catch((error) => {
            console.log(error, error.response)

            if (error.response && error.response.status === 422) {
              const fieldErrors = error.response.data.errors
              const errors = fieldErrors
                .map((field) => '- ' + field.msg)
                .join('<br>')
              this.newFileErrorMessage = errors
            } else {
              this.newFileErrorMessage =
                "Une erreur est survenue lors de l'envoi du fichier."
            }
          })
          .finally(() => {
            this.uploadingPercent = null
          })
      },
      handleFileUpload(type) {
        if (type === 'images') {
          this.fileImages = this.$refs.fileImages.files[0]
        } else if (type === 'sounds') {
          this.fileSounds = this.$refs.fileSounds.files[0]
        } else if (type === 'videos') {
          this.fileVideos = this.$refs.fileVideos.files[0]
        } else if (type === 'games') {
          this.fileGames = this.$refs.fileGames.files[0]
        }
      },

      submitFile(type) {
        const token = localStorage.getItem('jwt')
        if (token) {
          let formData = new FormData()
          if (type === 'images') {
            formData.append('images', this.fileImages)
          } else if (type === 'sounds') {
            formData.append('sounds', this.fileSounds)
          } else if (type === 'videos') {
            formData.append('videos', this.fileVideos)
          } else if (type === 'games') {
            formData.append('games', this.fileGames)
          }

          axios
            .post('/medias/upload/' + type, formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: 'Bearer ' + token,
              },
            })
            .then(() => {
              console.log(type + ' file uploaded')
              this.$notify({
                type: 'success',
                title: 'Upload réalisé avec succès',
                text: 'Les ' + type + ' ont été mis à jour.',
              })
            })
            .catch((err) => {
              console.log('Failed to upload ' + type + ' file', err)
              this.$notify({
                type: 'error',
                title: "Echec de l'upload",
                text: err ? err : 'Erreur inconnue.',
              })
            })
        } else {
          this.$router.push('/login')
        }
      },
      onVersionDelete() {
        this.fetchVersions()
        this.fetchAnalytics()
      },
    },
  }
</script>

<style></style>
